/**
 * What we dial: digits, * and #, with + allowed only at the start. Everything
 * else (spaces, dashes, brackets) goes. "00…" is left alone: MagnusBilling's
 * dial rules decide what prefixes mean.
 */
export function normalizeNumber(input: string): string {
  const trimmed = input.trim();
  const plus = trimmed.startsWith('+') ? '+' : '';
  return plus + trimmed.replace(/[^0-9*#]/g, '');
}

/** Characters a person may type into the number box. */
export function cleanTyped(input: string): string {
  return input.replace(/[^0-9*#+ ()-]/g, '').slice(0, 40);
}

export function formatDuration(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

const HIDDEN = new Set(['', 'anonymous', 'unknown', 'restricted', 'private', 'unavailable']);

/** Who is calling, for display: no empty or repeated parts, hidden numbers as "Unknown caller". */
export function callerOf(rawNumber: string | null | undefined, rawName: string | null | undefined): { number: string; name: string | null } {
  const num = (rawNumber ?? '').trim();
  const nm = (rawName ?? '').trim().replace(/^"|"$/g, '');
  const hiddenNum = HIDDEN.has(num.toLowerCase());
  const hiddenName = HIDDEN.has(nm.toLowerCase());
  if (hiddenNum) return { number: hiddenName ? 'Unknown caller' : nm, name: null };
  // a "name" that is only the number again (often with or without +, spaces, dashes) is not a name
  const digits = (x: string) => x.replace(/\D/g, '');
  const sameAsNumber = nm.toLowerCase() === num.toLowerCase() || (/^[\d\s+().-]+$/.test(nm) && digits(nm) !== '' && digits(nm) === digits(num));
  return { number: num, name: hiddenName || sameAsNumber ? null : nm };
}

/**
 * One of the user's own numbers (DIDs are stored with the country code) for
 * reading: "12012858570" -> "+1 (201) 285-8570" (North America), other long
 * numbers "+<digits>"; short codes and anything else as given.
 */
export function formatPhone(raw: string | null | undefined): string {
  const s = (raw ?? '').trim();
  const d = s.replace(/\D/g, '');
  if (!d || /[*#]/.test(s)) return s;
  if (d.length === 11 && d.startsWith('1')) return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  if (d.length >= 8) return `+${d}`;
  return s;
}

/**
 * A number for reading in call screens: US/Canada numbers with or without the
 * leading 1 as "+1 (209) 348-3263", others as formatPhone does.
 */
export function displayNumber(raw: string | null | undefined): string {
  const d = (raw ?? '').replace(/\D/g, '');
  if (d.length === 10 && /^[2-9]/.test(d) && !/[*#]/.test(raw ?? '')) return formatPhone(`1${d}`);
  return formatPhone(raw);
}
